import React, { useEffect } from "react";
import { observable, computed, reaction, action } from "mobx";
import { observer, useLocalObservable } from "mobx-react";
import { Dropdown, Checkbox, Input, Button, Empty, Tooltip } from "antd";
import { List } from "react-virtualized";
import { v1 as uuid } from "uuid";
import { translateKey as t } from "../../locales/translation";
import _ from "lodash";

import "./OFMultiSelect.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown } from "@fortawesome/free-solid-svg-icons";

interface OFMultiSelectProps {
  // Mandatory
  data: any;
  renderItem: (item) => any | Function | JSX.Element | String; //qwerty
  selectedItems: Array<string>; // Array of accessors from the data
  onSelect: (item, state, all?) => void;

  // Optional
  accessor?: (item) => void; // Use a custom data pointer instead of the default _id
  disabledDropdown?: boolean;
  readOnly?: boolean;
  disabledItem?: (item) => boolean;
  placeholder?: string;
  btnPlaceholder?: string | JSX.Element;
  menuPlacement?: "bottomRight" | "topLeft" | "top" | "topRight" | "bottomLeft" | "bottom";
  showSearch?: boolean;
  size?: "small" | "middle" | "large";
  style?: object;
  menuWidth?: number;
  canSelectAll?: boolean;
  removeResetAll?: boolean;
  allowClear?: boolean;
  icon?: JSX.Element;
}

const OFMultiSelect: React.FC<OFMultiSelectProps> = observer(
  ({
    data,
    renderItem,
    selectedItems,
    onSelect,
    accessor,
    disabledDropdown,
    readOnly,
    disabledItem,
    placeholder,
    btnPlaceholder,
    menuPlacement,
    showSearch,
    size,
    style,
    menuWidth,
    canSelectAll,
    removeResetAll,
    allowClear,
    icon,
  }) => {
    const state = useLocalObservable(() => ({
      classId: uuid(),
      inputRef: null,
      filterValue: "",
      dropDownActive: false,
      menuWidth: menuWidth || 200,
      setInputRef(value: any) {
        this.inputRef = value;
      },
      setFilterValue(value: string) {
        this.filterValue = value;
      },
      setDropDownActive(value: boolean) {
        this.dropDownActive = value;
      },
      setMenuWidth(value: number) {
        this.menuWidth = value;
      },
      //----------------------------------------------------
      data: data,
      selectedItems: selectedItems,
      showSelectedOnly: false,

      setData: action(function (newData: any) {
        this.data = newData;
      }),
      setSelectedItems: action(function (newSelectedItems: Array<string>) {
        this.selectedItems = newSelectedItems;
      }),
      setShowSelectedOnly: action(function (newShowSelectedOnly: boolean) {
        this.showSelectedOnly = newShowSelectedOnly;
      }),

      get filteredData() {
        if (!showSearch) {
          return this.filteredOnlySelected;
        }

        return this.filteredOnlySelected.filter((item) => {
          const val: any = renderItem(item);

          return val && val.toString().toLowerCase().includes(this.filterValue.toLowerCase());
        });
      },

      get filteredOnlySelected() {
        if (!this.showSelectedOnly) {
          return this.data;
        }

        return this.data.filter((obj) => this.selectedItems.includes(getDataValue(obj)));
      },

      get menuDataLength() {
        return this.filteredData.length;
      },

      get menuHeight() {
        const itemHeight = this.menuDataLength * 32;

        if (itemHeight === 0) {
          return 120;
        }

        return 256 > itemHeight ? itemHeight : 256;
      },

      get indeterminate() {
        if (this.selectedItems.length === 0) {
          return false;
        }

        return this.selectedItems.length !== this.filteredData.length;
      },
    }));

    React.useEffect(() => {
      const { setData, setSelectedItems, setInputRef } = state;

      setData(data);
      setSelectedItems(selectedItems);
      setInputRef(document.getElementById(state.classId));
      // updateMenuWidth();
    }, [state.classId, data, selectedItems, state, state.showSelectedOnly]);
    //------------------------------------------------------------------------------------------------
    const getDataValue = (data) => {
      if (accessor) {
        return accessor(data);
      }

      return _.get(data, "_id");
    };

    const updateMenuWidth = () => {
      if (!menuWidth && state.inputRef) {
        state.setMenuWidth(state.inputRef.clientWidth);
      }
    };

    const controlDropDown = () => {
      if (state.dropDownActive) {
        state.setDropDownActive(false);
        state.inputRef.blur();
      } else {
        state.setDropDownActive(true);
      }

      state.setFilterValue("");
    };

    //-------------Renders--------------------------------------------------------------------------------------

    const noContentRenderer = () => {
      return (
        <div>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ width: state.menuWidth }} />
        </div>
      );
    };

    const rowRenderer = (props) => {
      const index = props.index;
      const item = state.filteredData[index];
      const selected = selectedItems.includes(getDataValue(item));

      return (
        <Checkbox
          data-e2e="OFMultiSelect-list-checkbox"
          key={index}
          className={selected ? "item selected" : "item"}
          checked={selected}
          style={props.style}
          disabled={disabledItem ? disabledItem(item) : readOnly}
          onChange={(value) => {
            if (onSelect) {
              onSelect(item, value.target.checked);
            }
          }}
        >
          {renderItem(item)}
        </Checkbox>
      );
    };

    const RenderList = observer(() => {
      return (
        <div style={{ width: state.menuWidth }}>
          <div className="dropDownOptions">
            <div className="flex-1">
              {canSelectAll && (
                <Checkbox
                  key="all"
                  className="item"
                  indeterminate={state.indeterminate}
                  disabled={readOnly}
                  checked={selectedItems.length === state.filteredData.length}
                  onChange={(value) => {
                    if (onSelect) {
                      onSelect(state.filteredData, value.target.checked, true);
                    }
                  }}
                >
                  {t("lblAll", "All")}
                </Checkbox>
              )}
              {!removeResetAll && (
                <span
                  onClick={(e) => (readOnly ? e.preventDefault() : onSelect([], false, true))}
                  style={
                    selectedItems.length === 0 || readOnly
                      ? { color: "gray", cursor: "not-allowed" }
                      : { color: "#1890ff", cursor: "pointer" }
                  }
                >
                  {t("lblReset", "Reset")}
                </span>
              )}
            </div>
            <Tooltip title={t("lblShowHideSelectedOnly", "Show/hide selected only")} placement="topRight">
              <span
                onClick={() => {
                  state.setShowSelectedOnly(!state.showSelectedOnly);
                }}
                style={{ color: "#1890ff", cursor: "pointer" }}
              >
                {state.showSelectedOnly ? t("lblShowAll", "Show All") : t("lblShowSelected", "Show Selected")}
              </span>
            </Tooltip>
          </div>
          <List
            width={state.menuWidth}
            className="virtualizedDropDownList"
            height={state.menuHeight}
            rowCount={state.menuDataLength}
            rowHeight={32}
            rowRenderer={rowRenderer}
            noRowsRenderer={() => noContentRenderer()}
          />
        </div>
      );
    });

    updateMenuWidth();
    return (
      <Dropdown
        className={readOnly ? "OFMultiSelect OFVirtualizedDropDown readOnly" : "OFMultiSelect OFVirtualizedDropDown"}
        dropdownRender={() => <RenderList />}
        trigger={["click"]}
        overlayStyle={{ top: 30 }}
        overlayClassName="OFMultiSelect"
        placement={menuPlacement || "bottomLeft"}
        onOpenChange={() => controlDropDown()}
        open={state.dropDownActive}
        disabled={disabledDropdown}
        getPopupContainer={(triggerNode: HTMLElement) => triggerNode.parentNode as HTMLElement}
      >
        {showSearch ? (
          <Input
            placeholder={placeholder || t("lblSelectedCount", "Selected ({1})", [selectedItems.length])}
            onChange={(e) => state.setFilterValue(e.currentTarget.value)}
            style={style || {}}
            size={size || "middle"}
            id={state.classId}
            suffix={
              <FontAwesomeIcon
                onClick={(e) => (disabledDropdown ? e.preventDefault() : controlDropDown())}
                icon={faAngleDown}
                className={"inputArrow"}
              />
            }
            prefix={icon}
            value={state.filterValue}
            allowClear={allowClear || false}
            disabled={disabledDropdown}
          />
        ) : (
          <Button disabled={disabledDropdown} size={size || "middle"} style={style || {}} id={state.classId}>
            {btnPlaceholder ? (
              btnPlaceholder
            ) : (
              <>
                {t("lblSelectedCount", "Selected {1}", [selectedItems.length])}
                <FontAwesomeIcon icon={faAngleDown} style={{ marginLeft: 8 }} />
              </>
            )}
          </Button>
        )}
      </Dropdown>
    );
  }
);
export { OFMultiSelect };
